import { css } from '@emotion/react'
import { Heading, hexToRGB, HFlow, Icon, Tag, Theme, useTheme } from 'bold-ui'
import { AccordionButton } from 'components/accordion/AccordionButton'
import { SideMenuList } from 'components/layout/SideMenu/SideMenuList'
import { ReactNode } from 'react'
import { AccordionItem, AccordionItemPanel } from 'react-accessible-accordion'

interface SideMenuAccordionItemProps {
  uuid?: string
  title: string
  index: number
  totalItems: number
  children: ReactNode
  isMenuActive: boolean
  firstItemTagContent?: string
  hasHeader?: boolean
  onClickAccordionItem?(): void
  onClickAccordionButton: () => void
}

export function SideMenuAccordionItem(props: SideMenuAccordionItemProps) {
  const {
    uuid,
    title,
    index,
    totalItems,
    children,
    isMenuActive,
    firstItemTagContent,
    hasHeader = false,
    onClickAccordionItem,
    onClickAccordionButton,
  } = props

  const theme = useTheme()

  const isFirst = index === 0
  const isLast = index === totalItems - 1

  const styles = createStyles(theme, isFirst, isLast, isMenuActive, hasHeader)

  return (
    <AccordionItem uuid={uuid} onClick={onClickAccordionItem} css={styles.accordionItem}>
      <AccordionButton
        style={styles.accordionButton}
        wrapperStyle={styles.accordionButtonWrapper}
        onClick={onClickAccordionButton}
      >
        <HFlow alignItems='center' justifyContent='space-between' style={styles.header}>
          <HFlow hSpacing={0.5} alignItems='center'>
            <Icon icon='angleDown' />
            <Heading level={5} color='inherit'>
              {title}
            </Heading>
          </HFlow>

          {firstItemTagContent && index === 0 && (
            <div css={styles.tagAlign}>
              <Tag height='1.5rem' type='info'>
                {firstItemTagContent}
              </Tag>
            </div>
          )}
        </HFlow>
      </AccordionButton>
      <AccordionItemPanel css={styles.panel}>
        <SideMenuList>{children}</SideMenuList>
      </AccordionItemPanel>
    </AccordionItem>
  )
}

const createStyles = (theme: Theme, isFirst: boolean, isLast: boolean, isMenuActive: boolean, hasHeader: boolean) => ({
  panel: css`
    border: 1px ${theme.pallete.divider} solid;
    border-top-width: 0;
  `,
  accordionButtonWrapper: css`
    padding-bottom: 0;
  `,
  accordionButton: css`
    padding: 0.5rem 0.7rem;
    overflow: hidden;
    border: 1px ${theme.pallete.divider} solid;
    border-radius: ${isLast ? `0 0 ${theme.radius.input}px ${theme.radius.input}px` : '0'};
    background-color: ${theme.pallete.gray.c100};
    margin: 0;
    margin-top: ${isFirst ? '0' : '-1px'};

    &:hover {
      border: 1px ${theme.pallete.divider} solid;
      background-color: ${hexToRGB(theme.pallete.gray.c20, 0.1)};
    }

    &:focus {
      border-radius: 2px;
      position: relative;
      z-index: 1;
    }
  `,
  header: css`
    color: ${isMenuActive ? theme.pallete.primary.main : 'inherit'};
    display: flex;
    gap: 0.2rem;

    ${theme.breakpoints.down('lg')} {
      flex-direction: column;
      align-items: flex-start;
    }
  `,
  tagAlign: css`
    ${theme.breakpoints.down('lg')} {
      margin-left: 2rem;
    }
  `,
  accordionItem: css`
    margin-top: ${isFirst && !hasHeader ? '5px' : '0'};
  `,
})
